import * as XLSX from 'xlsx';

// ล้างอักขระล่องหนที่ติดมากับ copy จากแชท/ไฟล์ PI: zero-width/BOM ทิ้ง, NBSP → ช่องว่าง
// ไม่ล้าง = ค้น/จับคู่รหัสแล้ว "ไม่พบ" เงียบๆ (เคสจริง 21 ก.ค.: SKU วางจาก LINE ติด U+200B)
export const cleanText = (s) => String(s ?? '').replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\u00A0/g, ' ');

// System fields a PI column can map to (see supplier_column_maps.mapping).
export const SYSTEM_FIELDS = [
  ['supplier_product_code', 'รหัสซัพพลายเออร์'],
  ['description', 'ชื่อสินค้า'],
  ['qty', 'จำนวน'],
  ['unit_price', 'ราคา/หน่วย'],
  ['amount', 'รวม'],
];
export const REQUIRED_FIELDS = ['supplier_product_code', 'qty'];

const GUESS = [
  [/(item\s*)?(code|no\.?|number)$|^sku$|品番|part\s*no|รหัส/i, 'supplier_product_code'],
  [/desc|name|product|item(?!.*(code|no))|商品|品名|รายการ|ชื่อ/i, 'description'],
  [/q'?ty|quantity|数量|จำนวน|pcs/i, 'qty'],
  [/unit\s*price|price|単価|ราคา|@/i, 'unit_price'],
  [/amount|total|金額|รวม|sum/i, 'amount'],
];

// Shared: raw grid (string[][]) → { headers, rows }.
// Header row = a known index (PDF path passes the table-header row it anchored
// columns on) or, for spreadsheets, the densest row within the first 15
// (PI files often have a preamble above the table).
export function finalizeGrid(grid, headerIdxHint = null) {
  if (!grid.length) throw new Error('ไฟล์ว่าง — ไม่พบข้อมูล');
  let headerIdx = headerIdxHint;
  if (headerIdx == null) {
    headerIdx = 0;
    let best = -1;
    grid.slice(0, 15).forEach((r, i) => {
      const filled = r.filter(Boolean).length;
      if (filled > best) { best = filled; headerIdx = i; }
    });
  }
  const headers = grid[headerIdx].map((h, i) => h || `คอลัมน์ ${i + 1}`);
  const rows = grid.slice(headerIdx + 1).filter(r => r.some(Boolean));
  return { headers, rows };
}

// Parse Excel/CSV/PDF → { headers: string[], rows: string[][] }
export async function parseFile(file) {
  const buf = await file.arrayBuffer();
  if (/\.pdf$/i.test(file.name) || file.type === 'application/pdf') {
    const { grid, headerIdx } = await pdfToGrid(buf);
    return finalizeGrid(grid, headerIdx);
  }
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const grid = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' })
    .map(r => r.map(c => String(c ?? '').trim()));
  return finalizeGrid(grid);
}

/* ---------- PDF → grid (text-based PDFs only; scans need OCR — not supported) ----------
   pdf.js gives each text chunk with x/y page coordinates. We rebuild the table:
   rows = items clustered by y; columns = x anchors taken from the densest row
   (the header row of the table), every cell snapped to its nearest anchor. */

async function pdfToGrid(buf) {
  // WebKit/Safari: ReadableStream is not async-iterable, but pdf.js iterates it
  // ("undefined is not a function (near '...value of readableStream...')").
  if (typeof ReadableStream !== 'undefined' && !ReadableStream.prototype.values) {
    ReadableStream.prototype.values = function () {
      const reader = this.getReader();
      return {
        next: () => reader.read(),
        return: v => { reader.releaseLock(); return Promise.resolve({ done: true, value: v }); },
        [Symbol.asyncIterator]() { return this; },
      };
    };
    ReadableStream.prototype[Symbol.asyncIterator] = ReadableStream.prototype.values;
  }
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const pages = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const tc = await page.getTextContent();
    pages.push(tc.items
      .filter(i => i.str && i.str.trim())
      .map(i => ({ str: i.str.trim(), x: i.transform[4], y: i.transform[5], w: i.width || 0 })));
  }
  const total = pages.reduce((t, items) => t + items.length, 0);
  if (total < 5) throw new Error('PDF นี้ไม่มีข้อความ (น่าจะเป็นไฟล์สแกน) — ใช้ Excel/CSV หรือ PDF ที่ export จากระบบแทน');
  return pagesToGrid(pages);
}

// Pure (testable): per-page positioned text items → { grid, headerIdx }.
export function pagesToGrid(pages, yTol = 4) {
  // 1) cluster items into visual rows by y (PDF y grows upward)
  const allRows = [];
  for (const items of pages) {
    const sorted = [...items].sort((a, b) => b.y - a.y || a.x - b.x);
    const rows = [];
    for (const it of sorted) {
      const row = rows.find(r => Math.abs(r.y - it.y) <= yTol);
      if (row) { row.items.push(it); row.y = (row.y * row.items.length + it.y) / (row.items.length + 1); }
      else rows.push({ y: it.y, items: [it] });
    }
    allRows.push(...rows.map(r => { r.items.sort((a, b) => a.x - b.x); return r.items; }));
  }
  if (!allRows.length) return { grid: [], headerIdx: 0 };

  // merge runs on one line whose gap < g (fragments of the same cell)
  const mergeRow = (cells, g) => {
    const out = [];
    for (const it of cells) {
      const prev = out[out.length - 1];
      if (prev && it.x - (prev.x + prev.w) < g) {
        prev.str += (it.x - (prev.x + prev.w) > 0.5 ? ' ' : '') + it.str;
        prev.w = it.x + it.w - prev.x;
      } else out.push({ ...it });
    }
    return out;
  };

  // 2) tight merge first (only truly touching runs; keeps real cells apart even
  //    when columns sit close — seen on a real Snow Peak PI where the Amount
  //    cell sat 3pt from the Remarks cell)
  let rows = allRows.map(r => mergeRow(r, 2));

  // 3) find the table header: prefer rows whose cells match known PI column
  //    patterns over rows that merely have many text fragments (shipping-terms
  //    paragraphs split into 10+ tiny cells by pdf.js)
  const scoreHeader = cells => {
    const used = new Set();
    let m = 0;
    for (const c of cells) {
      for (const [re, field] of GUESS) {
        if (!used.has(field) && re.test(c.str)) { used.add(field); m++; break; }
      }
    }
    return m;
  };
  let densest;
  let bestHScore = -1;
  for (const r of rows) {
    const hs = scoreHeader(r);
    if (hs >= 2 && (hs > bestHScore || (hs === bestHScore && r.length < densest.length))) {
      densest = r; bestHScore = hs;
    }
  }
  if (!densest) densest = rows.reduce((a, b) => (b.length > a.length ? b : a));
  let headerIdx = rows.indexOf(densest);

  // 3b) multi-line header: row above may hold extra columns or top halves of
  //     two-line labels (e.g. "Product\nName" above "\nNumber" → "Product Name")
  if (headerIdx > 0) {
    const above = mergeRow(rows[headerIdx - 1], 8);
    const hMinX = Math.min(...densest.map(c => c.x));
    const extra = above.filter(c => c.x + c.w < hMinX - 15);
    if (extra.length > 0) {
      const merged = [...extra];
      for (const hc of densest) {
        const ac = above.find(a => !extra.includes(a) && Math.abs(a.x - hc.x) < 25);
        merged.push(ac
          ? { str: ac.str + ' ' + hc.str, x: Math.min(ac.x, hc.x),
              w: Math.max(ac.x + ac.w, hc.x + hc.w) - Math.min(ac.x, hc.x) }
          : { ...hc });
      }
      merged.sort((a, b) => a.x - b.x);
      densest = mergeRow(merged, 6);
      rows[headerIdx] = densest;
    }
  }

  // 3c) if data rows consistently start left of the header (e.g. row-number
  //     column with no label), prepend a blank header column so the DP
  //     assignment can snap each cell to the right column
  if (headerIdx < rows.length - 1) {
    const hMinX = Math.min(...densest.map(c => c.x));
    let lc = 0, lx = 0;
    const sn = Math.min(6, rows.length - headerIdx - 1);
    for (let r = headerIdx + 1; r < headerIdx + 1 + sn; r++) {
      const f = rows[r]?.[0];
      if (f && f.x < hMinX - 25) { lc++; lx += f.x; }
    }
    if (lc >= sn * 0.5) {
      densest = [{ str: '', x: lx / lc, w: 15 }, ...densest];
      rows[headerIdx] = densest;
    }
  }

  const nCols = densest.length;

  // 4) rows with MORE cells than columns still contain split fragments —
  //    re-merge those rows with progressively wider gaps
  rows = rows.map((r, i) => {
    if (i === headerIdx) return r;
    let g = 4;
    while (r.length > nCols && g <= 14) { r = mergeRow(r, g); g += 4; }
    return r;
  });

  // 5) column x-ranges: seed from header, expand from data rows whose cells
  //    fall near a header center. Handles centered headers over left-aligned
  //    values and right-aligned numeric columns.
  const colStart = densest.map(c => c.x);
  const colEnd = densest.map(c => c.x + c.w);
  const hdrCenters = densest.map(c => c.x + c.w / 2);
  for (let ri = headerIdx + 1; ri < rows.length; ri++) {
    const row = rows[ri];
    if (row.length < nCols - 2) continue;
    for (const c of row) {
      let bi = 0, bd = Infinity;
      for (let i = 0; i < nCols; i++) {
        const d = Math.abs((c.x + c.w / 2) - hdrCenters[i]);
        if (d < bd) { bd = d; bi = i; }
      }
      if (bd < 50) {
        colStart[bi] = Math.min(colStart[bi], c.x);
        colEnd[bi] = Math.max(colEnd[bi], c.x + c.w);
      }
    }
  }
  // 6) merge adjacent cells that belong to the same column (e.g. split name
  //    "SPICE" + "CABIN" both nearest to Product Name header center)
  const nearCol = c => {
    let bi = 0, bd = Infinity;
    for (let i = 0; i < nCols; i++) {
      const d = Math.abs((c.x + c.w / 2) - hdrCenters[i]);
      if (d < bd) { bd = d; bi = i; }
    }
    return bi;
  };
  rows = rows.map(r => {
    if (r === densest || r.length <= 1) return r;
    const out = [];
    for (const c of r) {
      const prev = out[out.length - 1];
      if (prev && nearCol(prev) === nearCol(c) && c.x - (prev.x + prev.w) < 30) {
        prev.str += ' ' + c.str;
        prev.w = c.x + c.w - prev.x;
      } else out.push({ ...c });
    }
    return out;
  });

  const centers = colStart.map((s, i) => (s + colEnd[i]) / 2);
  const PAD = 4;
  const overlapOf = (c, i) =>
    Math.max(0, Math.min(c.x + c.w, colEnd[i] + PAD) - Math.max(c.x, colStart[i] - PAD));
  const closeness = (c, i) => 4 / (4 + Math.abs((c.x + c.w / 2) - centers[i]));
  const score = (c, i) => overlapOf(c, i) + closeness(c, i);
  const put = (out, i, s) => { if (s) out[i] = out[i] ? `${out[i]} ${s}` : s; };

  // split one text run that pdf.js fused across ≥2 columns back into pieces,
  // cutting at the word boundary nearest each covered column's start-x
  const splitSpanning = c => {
    const covered = [];
    for (let i = 0; i < nCols; i++) if (overlapOf(c, i) > 2) covered.push(i);
    if (covered.length < 2 || !/\s/.test(c.str)) return [c];
    const charW = c.w / Math.max(c.str.length, 1);
    const parts = [];
    let str = c.str, curX = c.x;
    for (const b of covered.slice(1)) {
      const cut = Math.round((colStart[b] - curX) / charW);
      if (cut <= 0 || cut >= str.length) break;
      let at = -1;
      for (let d = 0; d <= str.length; d++) {
        if (cut - d > 0 && str[cut - d] === ' ') { at = cut - d; break; }
        if (cut + d < str.length && str[cut + d] === ' ') { at = cut + d; break; }
      }
      if (at < 0) break;
      const w1 = at * charW;
      parts.push({ str: str.slice(0, at).trim(), x: curX, w: w1 });
      curX += (at + 1) * charW;
      str = str.slice(at + 1);
    }
    parts.push({ str: str.trim(), x: curX, w: c.x + c.w - curX });
    return parts.filter(p => p.str);
  };

  // strictly-monotonic assignment: cells (left→right) take distinct columns in
  // order; DP maximizes total score. Fixes left-aligned values snapping to the
  // wrong centered header when geometry alone is ambiguous.
  const assignMonotonic = cells => {
    const k = cells.length;
    const dp = Array.from({ length: k + 1 }, () => Array(nCols + 1).fill(-Infinity));
    const take = Array.from({ length: k + 1 }, () => Array(nCols + 1).fill(false));
    for (let i = 0; i <= nCols; i++) dp[0][i] = 0;
    for (let j = 1; j <= k; j++) {
      for (let i = 1; i <= nCols; i++) {
        const skip = dp[j][i - 1];
        const use = dp[j - 1][i - 1] + score(cells[j - 1], i - 1);
        if (use >= skip) { dp[j][i] = use; take[j][i] = true; }
        else dp[j][i] = skip;
      }
    }
    const cols = Array(k).fill(0);
    let j = k, i = nCols;
    while (j > 0 && i > 0) {
      if (take[j][i]) { cols[j - 1] = i - 1; j--; i--; }
      else i--;
    }
    return cols;
  };

  const grid = rows.map(row => {
    const out = Array(nCols).fill('');
    const cells = row.flatMap(splitSpanning);
    if (cells.length <= nCols) {
      const cols = assignMonotonic(cells);
      cells.forEach((c, j) => put(out, cols[j], c.str));
    } else {
      // more fragments than columns even after re-merge — greedy fallback
      for (const c of cells) {
        let bi = 0, bs = -Infinity;
        for (let i = 0; i < nCols; i++) { const s = score(c, i); if (s > bs) { bs = s; bi = i; } }
        put(out, bi, c.str);
      }
    }
    return out;
  });
  return { grid, headerIdx };
}

export const fingerprint = headers => JSON.stringify(headers);

// Auto-guess a mapping { header: field } from header names (each field used once).
export function guessMapping(headers) {
  const mapping = {};
  const used = new Set();
  for (const h of headers) {
    for (const [re, field] of GUESS) {
      if (!used.has(field) && re.test(h)) { mapping[h] = field; used.add(field); break; }
    }
  }
  return mapping;
}

export const isComplete = mapping =>
  REQUIRED_FIELDS.every(f => Object.values(mapping ?? {}).includes(f));

// Numeric cell → number. Strips currency symbols/commas ('¥136,200' → 136200).
const num = v => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : null;
};

// Line categories — the kinds a PI line can be. Zort splits the same way:
// line producttype 0=Product / 1=Service; document-level ค่าส่ง (shippingamount),
// ส่วนลด (discount). Categories map onto those buckets so data reconciles 1:1
// when it reaches Zort / accounting.
export const LINE_CATEGORIES = [
  { key: 'product',  th: 'สินค้า',             hint: 'เข้าสต๊อก StockPoint', zort: 'Product' },
  // สินค้าเครม = ของจริงที่ซัพพลายเออร์ส่งชดเชย/แถม — เข้าสต๊อกเหมือนสินค้า แต่ราคาบังคับ 0 ไม่คิดมูลค่าซื้อ
  // (เก็บเป็น line_type='product' + fee_category='claim'; pd-api กรอง cost>0 อยู่แล้ว เลยไม่ทับต้นทุนจริงใน PD)
  { key: 'claim',    th: 'สินค้าเครม',          hint: 'เข้าสต๊อก ราคา 0 ไม่คิดมูลค่าซื้อ', zort: 'Product' },
  { key: 'service',  th: 'ค่าบริการ',           hint: 'ไม่เข้าสต๊อก',         zort: 'Service' },
  { key: 'shipping', th: 'ค่าขนส่ง',            hint: 'ไม่เข้าสต๊อก',         zort: 'ค่าส่ง' },
  { key: 'discount', th: 'ส่วนลด',              hint: 'หักออกจากยอด',        zort: 'ส่วนลด' },
  { key: 'duty',     th: 'ภาษีนำเข้า / อากร',   hint: 'ไม่เข้าสต๊อก',         zort: 'ค่าใช้จ่ายอื่น' },
  { key: 'fee',      th: 'ค่าธรรมเนียม',         hint: 'ไม่เข้าสต๊อก',         zort: 'ค่าใช้จ่ายอื่น' },
  { key: 'other',    th: 'อื่นๆ ไม่ใช่สินค้า',   hint: 'ไม่เข้าสต๊อก',         zort: 'ค่าใช้จ่ายอื่น' },
];

// keyword → category, checked in order (first hit wins)
const CAT_PATTERNS = [
  ['shipping', /freight|shipping|delivery|transport|carriage|forwarder|\bems\b|\bdhl\b|fedex/i],
  ['duty',     /duty|customs|tariff|clearance|import\s*tax/i],
  ['discount', /discount|rebate|deduction/i],
  ['fee',      /fee|charge|handling|packing|pallet|storage|surcharge|documentation|bank|remittance|insurance/i],
  ['service',  /service|inspection|installation|repair/i],
];
const detectCategory = text => {
  for (const [key, pat] of CAT_PATTERNS) if (pat.test(text)) return key;
  return null;
};

// footer/summary rows that are never importable lines
const EXCLUDE_PAT = /^\s*(sub\s*-?\s*total|total|grand\s+total|balance|amount\s+due|deposit|carried\s+forward|brought\s+forward)\b/i;

// Apply a mapping to raw rows → clean PI lines (skips subtotal/blank rows).
// Returns lines with lineType 'product' | 'fee' + feeCategory (LINE_CATEGORIES
// key, null for products). A line with both a supplier code and a qty is a
// product; anything else with an amount surfaces as a fee so the user can
// keep / recategorize / drop it — never silently swallowed.
export function applyMapping(headers, rows, mapping) {
  const idx = {};
  for (const [header, field] of Object.entries(mapping)) {
    const i = headers.indexOf(header);
    if (i >= 0) idx[field] = i;
  }
  return rows
    .map(r => ({
      code: cleanText(r[idx.supplier_product_code]).trim().replace(/\s*-\s*/g, '-'),
      desc: cleanText(r[idx.description]).trim(),
      qty: num(r[idx.qty]),
      unit: num(r[idx.unit_price]),
      amount: num(r[idx.amount]),
    }))
    .filter(l => {
      const text = l.desc || l.code;
      if (!text || EXCLUDE_PAT.test(text)) return false;
      // product-shaped — fractional qty with no price at all is parse noise
      // (e.g. bank-wire footer "ABA … BIC:…" digits landing in the qty column)
      if (l.code && l.qty > 0 && (l.qty >= 1 || l.unit || l.amount)) return true;
      if (l.amount) return true;              // priced non-product line (fees, discounts)
      return false;
    })
    .map(l => {
      const isProduct = !!(l.code && l.qty > 0);
      const qty = l.qty > 0 ? l.qty : 1;
      const unit = l.unit ?? (l.amount != null && qty ? l.amount / qty : 0);
      const amount = l.amount ?? (qty * unit);
      return {
        ...l, qty, unit, amount,
        lineType: isProduct ? 'product' : 'fee',
        feeCategory: isProduct ? null : (detectCategory(`${l.desc} ${l.code}`) ?? 'other'),
      };
    });
}
